import {
  Icon,
  cn,
  Spinner,
  Project,
  ProjectNew,
  DocumentSearchResult,
} from '@/dc-shared-web/src'
import { useIsProjectEditor } from '@/dc-shared-web/src/hooks/api/user/useIsProjectEditor'
import { useGetFolderTree } from '@/dc-shared-web/src/hooks/api/folder/useGetFolderForMove'
import {
  Folder,
  FoldersListItem,
  FolderTree,
} from '@/dc-shared-web/src/hooks/api/folder/types'
import { useGetFolderById } from '@/dc-shared-web/src/hooks/api/folder/useGetFolderByUuid'
import {
  faBriefcase,
  faCheck,
  faFolder,
  faHouseChimney,
  IconDefinition,
  faFileAlt,
} from '@document-crunch/pro-regular-svg-icons'
import { useRef, useEffect } from 'react'

const IconByLocationType: Record<LocationPathType, IconDefinition> = {
  folder: faFolder,
  project: faBriefcase,
  dashboard: faHouseChimney,
  document: faFileAlt,
}

export type LocationPathType = 'folder' | 'project' | 'dashboard' | 'document'

// TODO: id will be changed to only string in RR-1410
export type LocationType = {
  type: LocationPathType
  id: string | number
  name?: string
}

export const FolderSelectionTree = ({
  name,
  type,
  teamUuid,
  initialLocation,
  selectedLocation,
  onLocationChange,
  itemToMove,
}: {
  name: string
  type: LocationPathType
  teamUuid: string
  initialLocation: LocationType
  selectedLocation: LocationType | undefined
  onLocationChange: (location: LocationType) => void
  itemToMove:
    | Folder
    | Project
    | FoldersListItem
    | ProjectNew
    | DocumentSearchResult
    | undefined
}) => {
  const isProjectEditor = useIsProjectEditor()
  const { data: foldersAndProjects, isLoading: isLoadingFolders } =
    useGetFolderTree(teamUuid)

  const sortedFoldersAndProjects = foldersAndProjects?.sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  const { data: folderInfo } = useGetFolderById(
    teamUuid,
    itemToMove?.uuid || '',
    type === 'folder'
  )

  const hasProjectAncestor =
    folderInfo?.parent?.some((parent) => parent.type === 'project') || false

  const findLocationAndCheckProjects = (
    tree: FolderTree[],
    targetId: string
  ): { found: boolean; hasProjectDescendant: boolean } => {
    const search = (
      location: FolderTree
    ): { found: boolean; hasProjectDescendant: boolean } => {
      // Check if this is the target location
      if (location.uuid === targetId) {
        // Check if this location or any of its descendants are projects
        const hasProjectDescendant = (node: FolderTree): boolean => {
          if (node.type === 'project') return true
          return (
            node.children?.some((child) => hasProjectDescendant(child)) || false
          )
        }
        return {
          found: true,
          hasProjectDescendant: hasProjectDescendant(location),
        }
      }

      // Search children if they exist
      if (location.children) {
        for (const child of location.children) {
          const result = search(child)
          if (result.found) return result
        }
      }

      return { found: false, hasProjectDescendant: false }
    }

    // Search through the top level of the tree
    for (const location of tree) {
      const result = search(location)
      if (result.found) return result
    }

    return { found: false, hasProjectDescendant: false }
  }

  const { hasProjectDescendant } = foldersAndProjects
    ? findLocationAndCheckProjects(foldersAndProjects, itemToMove?.uuid || '')
    : { hasProjectDescendant: false }

  const isSelectedRow = (locationType: LocationPathType, rowId?: string) => {
    if (itemToMove?.uuid === rowId) {
      return false
    }

    const isCurrentLocationMatch =
      initialLocation.type === locationType &&
      initialLocation.id === rowId &&
      !selectedLocation

    const isSelectedLocationMatch =
      selectedLocation?.type === locationType && selectedLocation?.id === rowId

    return isCurrentLocationMatch || isSelectedLocationMatch
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const selectedRowRef = useRef<HTMLDivElement>(null)

  // Add useEffect to handle scrolling
  useEffect(() => {
    if (foldersAndProjects?.length === 0) return
    if (selectedRowRef.current && containerRef.current) {
      selectedRowRef.current.scrollIntoView({ block: 'center' })
    }
  }, [foldersAndProjects?.length])

  const Row = ({
    disabled = false,
    indentClass,
    locationId,
    locationPathType,
    title,
    style,
    dataTestId,
  }: {
    disabled?: boolean
    /* This class could be removed if we wrap the list of "Row"s with a div with margin-left */
    indentClass?: string
    locationId: string
    locationPathType: LocationPathType
    title: string
    style?: React.CSSProperties
    dataTestId?: string
  }): JSX.Element => {
    // Determine if this row is the initial location
    const isInitialLocation =
      initialLocation.type === locationPathType &&
      initialLocation.id === locationId

    return (
      <div
        ref={isInitialLocation ? selectedRowRef : undefined}
        style={style}
        className={cn(
          'tw-flex tw-justify-between tw-p-2 tw-items-center',
          indentClass,
          /* If the row is selected, we add some extra styles */
          isSelectedRow(locationPathType, locationId) &&
            'tw-bg-primary-300 hover:!tw-bg-primary-300',
          /* When disabled, there are no hover styles */
          disabled
            ? 'tw-text-neutral-300'
            : 'hover:tw-cursor-pointer hover:tw-bg-primary-50'
        )}
        onClick={
          /* If the row is disabled, we don't want to trigger the onClick */
          disabled
            ? undefined
            : () =>
                onLocationChange({
                  type: locationPathType,
                  id: locationId,
                  name: title,
                })
        }
        data-testid={dataTestId}
      >
        <div className='tw-flex tw-items-center'>
          <Icon
            icon={IconByLocationType[locationPathType]}
            className={cn(
              'tw-h-4 tw-mr-2',
              disabled ? 'tw-text-neutral-300' : 'tw-text-foreground'
            )}
          />
          <div className='tw-break-all'>{title}</div>
        </div>
        {isSelectedRow(locationPathType, locationId) && (
          <Icon icon={faCheck} className='tw-ml-2 tw-text-neutral-900' />
        )}
      </div>
    )
  }

  const FolderChildren = ({
    location,
    depth = 1,
    currentLocation,
    hasProjectAncestor,
    hasProjectDescendant,
  }: {
    location: FolderTree
    depth: number
    currentLocation: LocationType
    hasProjectAncestor: boolean
    hasProjectDescendant: boolean
  }) => {
    function checkProjectAncestor(
      targetUuid: string,
      tree: FolderTree[],
      parentIsProject: boolean = false
    ): boolean {
      for (const node of tree) {
        if (node.uuid === targetUuid) {
          return parentIsProject
        }

        if (node.children?.length) {
          const foundInChildren = checkProjectAncestor(
            targetUuid,
            node.children,
            parentIsProject || node.type === 'project'
          )
          if (foundInChildren) {
            return true
          }
        }
      }
      return false
    }

    function checkIsDescendant(
      targetUuid: string,
      itemToMoveUuid: string,
      tree: FolderTree[]
    ): boolean {
      // If we're checking the same item, it's not a descendant
      if (targetUuid === itemToMoveUuid) {
        return false
      }

      for (const node of tree) {
        // If this node is the item being moved, check its children
        if (node.uuid === itemToMoveUuid) {
          // Search through children to find target
          const searchChildren = (children: FolderTree[]): boolean => {
            for (const child of children) {
              if (child.uuid === targetUuid) {
                return true
              }
              if (child.children?.length) {
                if (searchChildren(child.children)) {
                  return true
                }
              }
            }
            return false
          }
          return node.children ? searchChildren(node.children) : false
        }

        // If not found at this level, recurse into children
        if (node.children?.length) {
          const foundInChildren = checkIsDescendant(
            targetUuid,
            itemToMoveUuid,
            node.children
          )
          if (foundInChildren) {
            return true
          }
        }
      }
      return false
    }

    const shouldDisableRow = (loc: FolderTree): boolean => {
      const newFolderHasProjectAncestor = checkProjectAncestor(
        loc.uuid,
        sortedFoldersAndProjects || []
      )
      const isDescendant = checkIsDescendant(
        loc.uuid,
        itemToMove?.uuid || '',
        sortedFoldersAndProjects || []
      )

      if (type === 'document') return false
      if (loc.uuid === itemToMove?.uuid) return true
      if (isDescendant) return true // Disable if location is descendant of item being moved
      if (type === 'project' && loc.type === 'project') return true
      if (type === 'project' && newFolderHasProjectAncestor) return true
      if (type === 'folder' && loc.type === 'project' && hasProjectDescendant)
        return true
      if (
        type === 'folder' &&
        loc.type === 'folder' &&
        newFolderHasProjectAncestor &&
        hasProjectDescendant
      )
        return true
      return false
    }

    return (
      <div key={location.uuid}>
        <Row
          locationPathType={location.type}
          locationId={location.uuid}
          title={location.name}
          style={{ paddingLeft: `${16 * depth}px` }}
          disabled={shouldDisableRow(location)}
          dataTestId={`${location.type}-location-row`}
        />
        {location.children?.map((childLocation) => (
          <div key={childLocation.uuid}>
            {childLocation.children?.length > 0 ? (
              <FolderChildren
                location={childLocation}
                depth={depth + 1}
                currentLocation={currentLocation}
                hasProjectAncestor={hasProjectAncestor}
                hasProjectDescendant={hasProjectDescendant}
              />
            ) : (
              <Row
                locationPathType={childLocation.type}
                locationId={childLocation.uuid}
                title={childLocation.name}
                disabled={shouldDisableRow(childLocation)}
                style={{ paddingLeft: `${16 * (depth + 1)}px` }}
                dataTestId={`${childLocation.type}-location-row`}
              />
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='tw-grid tw-gap-4'>
      {type !== 'document' && (
        <div>
          <p className='tw-text-2xs tw-mb-1'>
            {type === 'project' ? 'Project' : 'Folder'}
          </p>
          <div className='tw-flex'>
            <Icon
              icon={IconByLocationType[type]}
              className='tw-h-4 tw-mr-2 tw-text-foreground tw-mt-0.5'
            />
            <div className='tw-break-inner'>{name}</div>
          </div>
        </div>
      )}
      <div>
        <p className='tw-text-2xs tw-mb-1'>New Location</p>
        {isLoadingFolders ? (
          <div className='tw-flex tw-justify-center tw-items-center tw-h-52 tw-border'>
            <Spinner />
          </div>
        ) : (
          <div
            ref={containerRef}
            className='tw-w-full tw-border tw-h-64 tw-overflow-auto tw-text-sm'
          >
            {!isProjectEditor && (
              <Row
                title='Dashboard'
                locationPathType='dashboard'
                locationId={'dashboard'}
                dataTestId='dashboard-location-row'
              />
            )}
            {sortedFoldersAndProjects?.map((location) => (
              <FolderChildren
                currentLocation={initialLocation}
                location={location}
                key={location.uuid}
                depth={1}
                hasProjectAncestor={hasProjectAncestor}
                hasProjectDescendant={hasProjectDescendant}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
